import { quietHoursHeadline, quietHoursSupportingCopy } from "@healthos/shared";
import { ArrowUpRight } from "@phosphor-icons/react";
import type { ReactNode } from "react";
import { cn } from "../lib/cn";
import { SmoothImage } from "../primitives/smooth-image";
import { CARD_EYEBROW } from "../primitives/type";
import { PulseOrb } from "./pulse-orb";

export const QUIET_CARD_VIDEO_URL = "/videos/video_1.mp4";
export const QUIET_CARD_NIGHT_IMAGE_URL = "/night_sky.jpg";

/**
 * QuietCard, patient `/today` empty-state for the attention slot.
 *
 * Renders when Heartbeat has nothing to surface. The treatment mirrors
 * the mobile Today quiet state: muted nature video, pale fade, large
 * breathing pulse, and centered companion copy. When the API provides
 * watched signals, the card can still show them, but it never invents
 * fallback signal rows.
 *
 * When a plan item is offered, that item becomes the card: its poster
 * fills the frame, and the copy sits on a dark gradient in white.
 *
 * Sleeping (night) mode is built entirely on the `sleep-*` palette,
 * `shadow-sleeping`, and the `bg-gradient-sleeping*` utilities. No raw
 * hex, rgba, or off-scale font sizes.
 */

export interface QuietCardSignal {
  /** Plain-language label, e.g. "Sleep onset" or "Caffeine half-life". */
  label: string;
  /** Optional override for the right-aligned status. Defaults to "within range". */
  status?: string;
}

export interface QuietCardQuietHours {
  isActive: boolean;
  start: string;
  end: string;
  startsAtLabel: string;
  endsAtLabel: string;
}

/** One thing Heartbeat is watching for, and what it will ask when it fires. */
export interface QuietCardWatchRow {
  id: string;
  /** The behaviour it belongs to, for the row's label. */
  label: string;
  /** The reading itself: where the member stands on it right now. */
  reading: string;
  /** The one thing to do about it. */
  move: string;
  /** The only timing a rule can honestly promise: the day, or the night. */
  timing: string;
}

/** The last thing Heartbeat read, folded into the card that says it is quiet. */
export interface QuietCardHeartbeat {
  /** What it noticed, server-authored. */
  observation?: string | null;
  /** What it concluded from that. */
  context?: string | null;
  /** Server-authored relative age of the latest tick ("just now"). */
  /** What it is holding in mind now, present tense. Never a time. */
  reading?: string[];
  onOpenMap: () => void;
}

export interface QuietCardProps {
  className?: string;
  message?: string;
  accentMessage?: string | null;
  supporting?: string;
  meta?: string;
  quietHours?: QuietCardQuietHours | null;
  /**
   * Signals the system is actively monitoring. When omitted, the card
   * stays quiet instead of showing static defaults.
   */
  signals?: QuietCardSignal[];
  /**
   * Unfinished plan item offered while Heartbeat is quiet. The item
   * becomes the card: its poster is the backdrop, quiet copy sits on a
   * scrim, and this node is the title plus open action.
   */
  suggestion?: ReactNode;
  /** Poster for the offered plan item. Omit when the item has none. */
  suggestionBackdrop?: string | null;
}

/*
 * Mobile-scale pulse. Three staggered rings, a soft halo, and a solid
 * teal core make the quiet state feel active without becoming an alert.
 */
export function QuietCard({
  className,
  message = "Nothing to act on right now.",
  accentMessage = null,
  supporting = "If something changes, HealthOS will show one small action, right here.",
  meta,
  quietHours = null,
  signals = [],
  suggestion,
  suggestionBackdrop = null,
}: QuietCardProps) {
  const prefersReducedMotion =
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const sleeping = quietHours?.isActive === true;
  const offered = Boolean(suggestion) && !sleeping;
  const resolvedMessage = sleeping
    ? quietHoursHeadline({ end: quietHours.end, endsAtLabel: quietHours.endsAtLabel })
    : message;
  const resolvedAccentMessage = sleeping ? "You're all set for now." : accentMessage;
  const resolvedSupporting = sleeping ? quietHoursSupportingCopy() : supporting;

  return (
    <article
      className={cn(
        "relative flex h-full flex-col overflow-hidden rounded-2xl border p-6 sm:p-8 short:p-5",
        // The floors are there so the card never reads as a strip on a tall
        // screen. On a short one they are the reason Today runs past the fold.
        offered
          ? "min-h-[360px] sm:min-h-[420px] short:min-h-[300px]"
          : "min-h-[420px] sm:min-h-[500px] short:min-h-[340px]",
        sleeping
          ? "border-teal-200/20 bg-sleep-deep shadow-sleeping"
          : offered
            ? "border-transparent bg-navy shadow-[inset_0_1px_0_color-mix(in_srgb,var(--color-paper)_13%,transparent),inset_0_0_0_1px_color-mix(in_srgb,var(--color-paper)_7%,transparent),0_1px_2px_color-mix(in_srgb,var(--color-navy)_8%,transparent),0_6px_14px_-6px_color-mix(in_srgb,var(--color-navy)_20%,transparent),0_14px_26px_-14px_color-mix(in_srgb,var(--color-navy)_22%,transparent)]"
            : "border-mist-dark/55 bg-cream-warm shadow-card",
        className,
      )}
      aria-label="Quiet state"
    >
      {sleeping ? (
        <QuietNightBackdrop />
      ) : offered ? (
        <QuietOfferBackdrop
          poster={suggestionBackdrop}
          prefersReducedMotion={prefersReducedMotion}
        />
      ) : (
        <QuietDayBackdrop prefersReducedMotion={prefersReducedMotion} />
      )}

      <div
        className={cn(
          "relative z-10 flex min-h-0 flex-1 flex-col",
          offered
            ? "items-start justify-between gap-8 text-left"
            : "items-center justify-center gap-7 text-center",
        )}
      >
        {/* State first, then the offer. With the message pinned to the bottom by
            mt-auto, "Heartbeat is listening" sat at the top of a tall poster and
            "Nothing to act on right now" at the far end of it, so the two halves
            of one thought read as two dense blocks with an image between them. */}
        {sleeping ? (
          <SleepMoonOrb />
        ) : offered ? (
          <div className="flex items-center gap-3">
            <PulseOrb size="inline" onDark />
            {/* The one card eyebrow: the same setting as "Keeping an eye on"
                beneath it and the eyebrows on the rail cards beside it. */}
            <span className={cn(CARD_EYEBROW, "text-paper/70")}>Heartbeat · listening</span>
          </div>
        ) : (
          <PulseOrb />
        )}

        {offered ? (
          <div className="flex w-full max-w-[32rem] flex-col items-start gap-1.5">
            <QuietHeadline
              message={resolvedMessage}
              accent={resolvedAccentMessage}
              supporting={resolvedSupporting}
              offered
              sleeping={sleeping}
            />
          </div>
        ) : null}

        {offered ? (
          <div className="mt-auto w-full max-w-[32rem]">{suggestion}</div>
        ) : (
          <div className="flex max-w-[31rem] flex-col items-center gap-3">
            <QuietHeadline
              message={resolvedMessage}
              accent={resolvedAccentMessage}
              supporting={resolvedSupporting}
              offered={false}
              sleeping={sleeping}
            />
          </div>
        )}

        {sleeping && quietHours ? (
          <div className="inline-flex max-w-full items-center gap-3 rounded-full border border-sleep-accent/30 bg-sleep-deep/60 px-4 py-2 shadow-[0_18px_34px_-26px_color-mix(in_srgb,var(--color-sleep-deep)_92%,transparent)] backdrop-blur-sm">
            <span
              aria-hidden="true"
              className="grid h-5 w-5 place-items-center rounded-full bg-sleep-accent/18"
            >
              <span className="h-2.5 w-2.5 rounded-full bg-sleep-accent" />
            </span>
            <span className={cn(CARD_EYEBROW, "text-sleep-mist/60")}>Listening quietly</span>
            <span aria-hidden="true" className="h-5 w-px bg-sleep-accent/24" />
            <span className="font-sans text-xl italic leading-none text-sleep-cream">
              wakes {quietHours.endsAtLabel}
            </span>
          </div>
        ) : null}

        {!sleeping && !offered && signals.length > 0 ? (
          <div className="w-full max-w-lg rounded-xl border border-mist-dark/40 bg-cream-warm/50 px-4 py-3 backdrop-blur-sm">
            <p className={cn(CARD_EYEBROW, "mb-2.5 text-center text-gray-500")}>Signals at rest</p>
            <dl className="m-0 grid gap-2 p-0 sm:grid-cols-2">
              {signals.map((signal) => (
                <div key={signal.label} className="flex items-center gap-2.5 text-left text-sm">
                  <span
                    aria-hidden="true"
                    className="h-1.5 w-1.5 shrink-0 rounded-full bg-leaf-dark/55"
                  />
                  <dt className="text-gray-700">{signal.label}</dt>
                  <dd className={cn(CARD_EYEBROW, "ml-auto text-gray-500")}>
                    {signal.status ?? "within range"}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        ) : null}

        {meta ? (
          <p
            className={cn(
              CARD_EYEBROW,
              "m-0",
              sleeping ? "text-sleep-mist/52" : offered ? "text-paper/55" : "text-gray-500",
            )}
          >
            {meta}
          </p>
        ) : null}
      </div>
    </article>
  );
}

/** The card's state, in one voice: the line, its accent, and the line under it. */
function QuietHeadline({
  message,
  accent,
  supporting,
  offered,
  sleeping,
}: {
  message: string;
  accent: string | null;
  supporting: string;
  offered: boolean;
  sleeping: boolean;
}) {
  return (
    <>
      <p
        className={cn(
          "m-0",
          offered
            ? "font-sans text-base font-normal italic leading-[1.45]"
            : "font-title text-4xl font-medium italic leading-[1.15]",
          sleeping ? "text-sleep-cream" : offered ? "text-paper" : "text-navy",
        )}
      >
        <span className="block">{message}</span>
        {accent ? (
          <span className={cn("block", sleeping ? "text-sleep-accent" : "text-teal-700")}>
            {accent}
          </span>
        ) : null}
      </p>
      {supporting ? (
        <p
          className={cn(
            "m-0 font-sans text-base font-normal leading-[1.45]",
            offered ? "max-w-[42ch]" : "max-w-[34ch]",
            sleeping ? "text-sleep-mist/75" : offered ? "text-paper/75" : "text-gray-600",
          )}
        >
          {supporting}
        </p>
      ) : null}
    </>
  );
}

/** What Heartbeat last concluded, and the way into the map. */
export function HeartbeatFootnote({
  heartbeat,
  className,
  linkClassName,
  readVisible = true,
}: {
  heartbeat: QuietCardHeartbeat | null | undefined;
  className?: string;
  linkClassName?: string;
  /** False on a card that already says what Heartbeat decided in its headline. */
  readVisible?: boolean;
}) {
  if (!heartbeat) return null;
  const read = readVisible ? (heartbeat.observation ?? "").trim() : "";
  return (
    <div className={cn(CARD_EYEBROW, "flex w-full items-center justify-between gap-3", className)}>
      <span className="min-w-0 truncate">{read}</span>
      <button
        type="button"
        onClick={heartbeat.onOpenMap}
        className={cn(
          "inline-flex shrink-0 items-center gap-1.5 underline-offset-4 hover:underline",
          linkClassName,
        )}
      >
        See the map
        <ArrowUpRight size={12} weight="bold" aria-hidden="true" />
      </button>
    </div>
  );
}

function QuietNightBackdrop() {
  return (
    <>
      <img
        alt=""
        aria-hidden="true"
        className="absolute inset-0 h-full w-full object-cover opacity-70 motion-safe:animate-[quiet-night-pan_34s_ease-in-out_infinite_alternate]"
        src={QUIET_CARD_NIGHT_IMAGE_URL}
        style={{ filter: "saturate(0.72) contrast(0.84)" }}
      />
      <span aria-hidden="true" className="absolute inset-0 bg-sleep-deep/38" />
      <span aria-hidden="true" className="absolute inset-0 bg-gradient-sleeping" />
      <span aria-hidden="true" className="absolute inset-0 bg-gradient-sleeping-fade" />
    </>
  );
}

function QuietDayBackdrop({ prefersReducedMotion }: { prefersReducedMotion: boolean }) {
  return (
    <>
      <video
        aria-hidden="true"
        autoPlay={!prefersReducedMotion}
        className="absolute inset-0 h-full w-full object-cover opacity-70 motion-reduce:hidden"
        loop
        muted
        playsInline
        preload="auto"
        src={QUIET_CARD_VIDEO_URL}
        tabIndex={-1}
      />
      <span aria-hidden="true" className="absolute inset-0 bg-teal-700/5" />
      <span
        aria-hidden="true"
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(180deg, color-mix(in srgb, var(--color-cream-warm) 82%, transparent) 0%, color-mix(in srgb, var(--color-cream-warm) 42%, transparent) 24%, color-mix(in srgb, var(--color-cream-warm) 64%, transparent) 48%, color-mix(in srgb, var(--color-cream-warm) 96%, transparent) 70%, var(--color-cream-warm) 100%)",
        }}
      />
      <span
        aria-hidden="true"
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(circle at 50% 30%, color-mix(in srgb, var(--color-teal-500) 18%, transparent) 0%, color-mix(in srgb, var(--color-teal-500) 8%, transparent) 18%, transparent 42%), radial-gradient(circle at 50% 82%, color-mix(in srgb, var(--color-leaf-soft) 72%, transparent) 0%, transparent 40%)",
        }}
      />
      <span
        aria-hidden="true"
        className="absolute inset-0"
        style={{
          background: "color-mix(in srgb, var(--color-cream-warm) 42%, transparent)",
        }}
      />
      {/* Lands the bottom edge on plain cream, so a panel joined beneath shows no step. */}
      <span
        aria-hidden="true"
        className="absolute inset-0"
        style={{
          background: "linear-gradient(180deg, transparent 86%, var(--color-cream-warm) 100%)",
        }}
      />
    </>
  );
}

function QuietOfferBackdrop({
  poster,
  prefersReducedMotion,
}: {
  poster: string | null;
  prefersReducedMotion: boolean;
}) {
  return (
    <>
      {poster ? (
        <SmoothImage src={poster} alt="" priority frameClassName="absolute inset-0 bg-navy" />
      ) : (
        <span aria-hidden="true" className="absolute inset-0 bg-navy" />
      )}
      <video
        aria-hidden="true"
        autoPlay={!prefersReducedMotion}
        className="absolute inset-0 h-full w-full object-cover opacity-20 mix-blend-soft-light motion-reduce:hidden"
        loop
        muted
        playsInline
        preload="auto"
        src={QUIET_CARD_VIDEO_URL}
        tabIndex={-1}
      />
      <span
        aria-hidden="true"
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(90deg, color-mix(in srgb, var(--color-navy) 78%, transparent) 0%, color-mix(in srgb, var(--color-navy) 52%, transparent) 42%, color-mix(in srgb, var(--color-navy) 18%, transparent) 72%, transparent 100%)",
        }}
      />
      <span
        aria-hidden="true"
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(180deg, color-mix(in srgb, var(--color-navy) 42%, transparent) 0%, transparent 36%, color-mix(in srgb, var(--color-navy) 72%, transparent) 100%)",
        }}
      />
      <span
        aria-hidden="true"
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse 80% 70% at 0% 100%, color-mix(in srgb, var(--color-navy) 70%, transparent) 0%, transparent 58%)",
        }}
      />
    </>
  );
}

function SleepMoonOrb() {
  return (
    <span aria-hidden="true" className="relative grid h-40 w-40 shrink-0 place-items-center">
      <span
        className="absolute h-52 w-52 rounded-full motion-safe:animate-[quiet-halo_3.2s_ease-in-out_infinite]"
        style={{
          background:
            "radial-gradient(circle, color-mix(in srgb, var(--color-sleep-accent) 26%, transparent) 0%, color-mix(in srgb, var(--color-sleep-accent) 10%, transparent) 44%, transparent 74%)",
        }}
      />
      <span className="absolute h-24 w-24 rounded-full border border-sleep-accent/34" />
      <span
        className="absolute h-36 w-36 rounded-full border border-sleep-accent/42"
        style={{
          animation: "quiet-sleep-ring 3.4s cubic-bezier(0.33, 1, 0.68, 1) infinite",
        }}
      />
      <span
        className="absolute h-36 w-36 rounded-full border border-sleep-accent/28"
        style={{
          animation: "quiet-sleep-ring 3.4s cubic-bezier(0.33, 1, 0.68, 1) infinite",
          animationDelay: "1.1s",
        }}
      />
      <span className="absolute left-[92px] top-[18px] font-mono text-base font-bold text-sleep-accent motion-safe:animate-[quiet-sleep-z_3.2s_ease-out_infinite]">
        Z
      </span>
      <span className="absolute left-[108px] top-[34px] font-mono text-sm font-bold text-sleep-accent/75 motion-safe:animate-[quiet-sleep-z_3.2s_ease-out_infinite] [animation-delay:0.85s]">
        Z
      </span>
      <span
        className="relative z-10 grid h-16 w-16 place-items-center rounded-full bg-sleep-core motion-safe:animate-[quiet-core_3.2s_ease-in-out_infinite]"
        style={{
          boxShadow:
            "0 0 0 10px color-mix(in srgb, var(--color-sleep-accent) 14%, transparent), inset 0 0 0 1px color-mix(in srgb, var(--color-sleep-glow) 22%, transparent)",
        }}
      >
        <span className="relative h-8 w-8 overflow-hidden rounded-full bg-sleep-glow/85">
          <span className="absolute left-2 top-[-2px] h-9 w-9 rounded-full bg-sleep-core" />
        </span>
      </span>
    </span>
  );
}
